import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  CheckSquare,
  Hash,
  Loader2,
  Megaphone,
  PhoneCall,
  PhoneIncoming,
  Volume2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Stepper,
  useStepper,
  type StepDescriptor,
} from '@/components/ui/stepper';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import type { InboundComposeFormValues } from '../types';
import { StepBasics } from './steps/StepBasics';
import { StepVoiceFiles } from './steps/StepVoiceFiles';
import { StepRouting } from './steps/StepRouting';
import { StepDids } from './steps/StepDids';
import { StepNotifications } from './steps/StepNotifications';
import { StepReview } from './steps/StepReview';

const STEPS: StepDescriptor[] = [
  { id: 'basics', label: 'Basics', icon: Megaphone },
  { id: 'voiceFiles', label: 'Voice files', icon: Volume2 },
  { id: 'routing', label: 'Routing', icon: Hash },
  { id: 'dids', label: 'DIDs', icon: PhoneCall },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'review', label: 'Review', icon: CheckSquare },
];

interface Props {
  mode: 'create' | 'clone';
  initialValues: InboundComposeFormValues;
  isPending: boolean;
  onSubmit: (values: InboundComposeFormValues) => void;
}

/** Validate the current step. Returns an error message or null. */
function validateStep(
  step: number,
  v: InboundComposeFormValues,
): string | null {
  if (step === 0) {
    if (!v.campaignName.trim()) return 'Give the campaign a name.';
    if (v.retries === 0 && v.retryInterval > 0)
      return 'Set retries before adding a retry interval.';
    if (v.retries > 0 && v.retryInterval === 0)
      return 'Set a retry interval (minutes) for the retries.';
    return null;
  }

  if (step === 1) {
    if (!v.menuId) return 'Pick a menu voice file.';
    if (!v.noAgentId) return 'Pick a "no agent" voice file.';
    if (!v.menuWaitTime)
      return 'Menu wait time is required (1–10 seconds).';
    if (!v.rePrompt) return 'Re-prompt count is required (0–3).';
    return null;
  }

  if (step === 2) {
    if (v.dtmf.length === 0)
      return 'Select at least one DTMF digit.';
    if (v.groupRows.length === 0)
      return 'Bind at least one agent group to a DTMF digit.';
    return null;
  }

  if (step === 3) {
    if (v.selectedCLI.length === 0)
      return 'Pick at least one CLI (DID).';
    return null;
  }

  // Notifications + review have no blocking validations of their own.
  return null;
}

export function InboundComposeWizard({
  mode,
  initialValues,
  isPending,
  onSubmit,
}: Props) {
  const [values, setValuesRaw] = useState<InboundComposeFormValues>(initialValues);
  const setValues = (patch: Partial<InboundComposeFormValues>) =>
    setValuesRaw((prev) => ({ ...prev, ...patch }));

  const stepper = useStepper(STEPS.length);

  const handleNext = () => {
    const err = validateStep(stepper.current, values);
    if (err) {
      toast.error(err);
      return;
    }
    stepper.next();
  };

  const handleSubmit = () => {
    // Run every step's validator once more before submit, so users who
    // jumped backward then forward via the Stepper can't skip a check.
    for (let i = 0; i < STEPS.length - 1; i++) {
      const err = validateStep(i, values);
      if (err) {
        toast.error(err);
        stepper.goTo(i);
        return;
      }
    }
    onSubmit(values);
  };

  const stepBody = useMemo(() => {
    switch (stepper.current) {
      case 0:
        return (
          <StepBasics
            values={values}
            setValues={setValues}
            autoFocusName={mode === 'create'}
          />
        );
      case 1:
        return <StepVoiceFiles values={values} setValues={setValues} />;
      case 2:
        return <StepRouting values={values} setValues={setValues} />;
      case 3:
        return <StepDids values={values} setValues={setValues} />;
      case 4:
        return <StepNotifications values={values} setValues={setValues} />;
      case 5:
        return <StepReview values={values} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepper.current, values, mode]);

  return (
    <ScrollPage>
      <PageHeader
        title={mode === 'create' ? 'New Inbound Campaign' : 'Clone Inbound Campaign'}
        description="Set up an inbound campaign: callers dial a DID, hear the menu, press a DTMF digit, and get patched to an agent group."
        actions={
          <Button asChild variant="outline">
            <Link to="/inbound-campaign-list">
              <ArrowLeft className="h-4 w-4" />
              Back to inbound campaigns
            </Link>
          </Button>
        }
      />

      <Stepper
        steps={STEPS}
        current={stepper.current}
        completed={stepper.completed}
        onStepClick={stepper.goTo}
      />

      <div className="mt-6 space-y-4">{stepBody}</div>

      <div className="mt-6 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          onClick={stepper.prev}
          disabled={stepper.isFirst || isPending}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        {stepper.isLast ? (
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PhoneIncoming className="h-4 w-4" />
            )}
            {mode === 'create' ? 'Create campaign' : 'Clone campaign'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={isPending}>
            Next
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </ScrollPage>
  );
}
